import React, { useState } from "react";
import PropTypes from "prop-types";
import { useUser } from "../../../../context/UserContext";
import { deleteTag } from "../../../../services/TagsService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import CustomSvg from "../../../../components/common/custom-svg/custom-svg";
import ConfirmDeleteIcon from "../../../../assets/icons-v2/confirm_delete.png";
import DeleteSuccessAnimation from "./CustomFeed/components/DeleteSuccessAnimation";
import ConfirmationModal from "../../../../components/common/ConfirmationModal";

const DeleteTag = ({ tag, propertyId, onSuccess, onCancel, open }) => {
    const { userState } = useUser();
    const queryClient = useQueryClient();
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

    const deleteTagMutation = useMutation({
        mutationFn: () => deleteTag(userState?.id, propertyId, tag?.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["propertyTags", propertyId] });
            queryClient.invalidateQueries({ queryKey: ["live-variant-listingFeed-properties"] });
            queryClient.invalidateQueries({ queryKey: ["live-variant-listingPage-property", propertyId] });
            setShowDeleteSuccess(true);
            if (onSuccess) onSuccess(true);
        },
        onError: (error) => {
            console.error("Error deleting tag:", error);
            toast.error(error.message || "Failed to delete tag");
            if (onCancel) onCancel();
        },
    });

    const handleConfirmDelete = () => {
        if (tag) {
            deleteTagMutation.mutate();
        }
    };

    const handleCancelDelete = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            {/* Delete Confirmation Modal */}
            {open && !showDeleteSuccess && (
                <ConfirmationModal
                    show={open}
                    title="Delete"
                    icon={<CustomSvg src={ConfirmDeleteIcon} size={{ width: 59, height: 59 }} />}
                    message={
                        <>
                            You sure want to Delete <span className="tw-font-bold tw-italic tw-text-[#6C26C4]">'#{tag?.name}'</span> tag?
                        </>
                    }
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                    confirmLabel="Delete"
                    cancelLabel="No, Keep it"
                    loading={deleteTagMutation.isPending}
                />
            )}
            {/* Delete Success Animation */}
            {showDeleteSuccess && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-30 tw-z-50 tw-flex tw-items-end md:tw-items-center tw-justify-center">
                    <div className="tw-w-full tw-max-w-md tw-bg-white tw-rounded-t-2xl md:tw-rounded-2xl tw-shadow-lg tw-px-4 tw-py-8 tw-flex tw-flex-col tw-items-center tw-mx-auto tw-min-h-[350px]">
                        <DeleteSuccessAnimation
                            message={
                                <>
                                    <span className="tw-italic tw-text-[#6C26C4]">'#{tag?.name}'</span>{" "}
                                    <span className="tw-font-normal">tag</span> Deleted
                                </>
                            }
                            onDone={() => {
                                setShowDeleteSuccess(false);
                                if (onCancel) onCancel();
                            }}
                            buttonLabel="Continue"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

DeleteTag.propTypes = {
    tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }),
    propertyId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
    open: PropTypes.bool,
};

export default DeleteTag; 